import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { stripLocalePrefix, localizePath } from "../i18n/localePath";
import { SITE, NOINDEX, V3_EN_READY, seoFor, organizationLd, websiteLd, faqLd, breadcrumbLd } from "../data/seo";
import { useVersion } from "../version/VersionContext";
import { V4_EN_INDEX } from "../v4/i18n/slugs.js";
import { v3Page, V3_SECTIONS } from "../v3/pages.js";

// Setzt je Seite Beschreibung, Canonical, Sprachvarianten, Open Graph und strukturierte Daten.
// Dieselben Werte schreibt tools/seo/prerender-head.mjs beim Build schon in das statische HTML.

const setMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!content) { el && el.remove(); return; }
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
};

const setLink = (rel, href, hreflang) => {
    const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(sel);
    if (!href) { el && el.remove(); return; }
    if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); hreflang && el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
    el.setAttribute("href", href);
};

const setJsonLd = (data) => {
    document.head.querySelectorAll('script[data-seo="ld"]').forEach((s) => s.remove());
    data.forEach((d) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.seo = "ld";
        s.textContent = JSON.stringify(d);
        document.head.appendChild(s);
    });
};

// Version 3: FAQ der Landingpages aus src/v3/content/landing.js (Vertrag: { de: { "<pfad>": { faq: [{ q, a }] } } }).
// Über require.context geladen, damit der Build auch ohne die Datei läuft (sie entsteht in Phase B bei B2).
let V3_LANDING = null;
try {
    const ctx = require.context("../v3/content", false, /^\.\/landing\.js$/);
    const key = ctx.keys()[0];
    if (key) { const mod = ctx(key); V3_LANDING = mod?.default || mod || null; }
} catch { V3_LANDING = null; }
const v3LandingFaq = (lang, path) => {
    const list = V3_LANDING?.[lang]?.[path]?.faq || [];
    return Array.isArray(list) ? list.filter((f) => f && typeof f.q === "string" && f.q.trim() && typeof f.a === "string" && f.a.trim()) : [];
};

// Version 4: englische Adressen über localizePath (src/v4/i18n/slugs.js), sonst wie bisher
export const urlFor = (path, lang) => (lang === "en" ? `${SITE.url}${path === "/" ? "/en" : localizePath(path, "en")}` : `${SITE.url}${path}`);

const SeoLegacy = () => {
    const { pathname } = useLocation();
    const { lang: contentLang, uiLang, t } = useLanguage();
    // Version 4: Sprache der Adresse für Adressen und Index, Texte aus der deutschen Quelle (übersetzt src/v4/i18n)
    const lang = uiLang || contentLang;
    const { version } = useVersion();

    useEffect(() => {
        const { routes: ROUTES, descriptions: DESCRIPTIONS, titles: V_TITLES } = seoFor(version);
        const raw = stripLocalePrefix(pathname);
        const path = raw === "/" ? raw : raw.replace(/\/$/, "");
        // Version 3 und 4 kennen nur die Adressen aus src/v3/pages.js
        const siteV34 = version === "3" || version === "4";
        const known = siteV34 ? ROUTES.includes(path) : ROUTES.includes(path) || /^\/careers\/[^/]+$/.test(path) || (version === "1" && /^\/insights\/(articles|news)\/[^/]+$/.test(path));
        // Platzhalterseiten (built: false) und englische Seiten, solange Englisch Platzhalter ist, nicht in den Index
        const v3Hold = siteV34 && known && (!v3Page(path)?.built || (version === "3" && lang === "en" && !V3_EN_READY) || (version === "4" && lang === "en" && !V4_EN_INDEX));
        const noindex = !known || v3Hold || NOINDEX.some((r) => r.test(path));
        // hreflang en nur mit echtem englischem Inhalt (EN_READY in src/v3/seo.js)
        const enAlt = !siteV34 || (version === "4" ? V4_EN_INDEX : V3_EN_READY);
        const dl = version === "4" ? "de" : lang;
        const desc = DESCRIPTIONS[dl]?.[path] || DESCRIPTIONS[dl]?.["/"];

        setMeta("name", "description", desc);
        setMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
        setLink("canonical", noindex ? null : urlFor(path, lang));
        setLink("alternate", noindex ? null : urlFor(path, "de"), "de");
        setLink("alternate", noindex || !enAlt ? null : urlFor(path, "en"), "en");
        setLink("alternate", noindex ? null : urlFor(path, "de"), "x-default");

        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", SITE.name);
        setMeta("property", "og:title", document.title);
        setMeta("property", "og:description", desc);
        setMeta("property", "og:url", urlFor(path, lang));
        setMeta("property", "og:image", SITE.image);
        setMeta("property", "og:locale", lang === "en" ? "en_US" : "de_DE");
        setMeta("name", "twitter:card", "summary_large_image");

        const ld = [organizationLd(lang)];
        if (path === "/") ld.push(websiteLd(lang));
        // Version 3 und 4: strukturierte Daten nur für gebaute Seiten aus der Seitenliste.
        if (siteV34) {
            const page = known ? v3Page(path) : null;
            if (page?.built && path !== "/") {
                const k = lang === "en" ? "en" : "de";
                const sec = page.menu ? V3_SECTIONS.find((s) => s.id === page.menu) : null;
                const items = [{ name: SITE.name, url: urlFor("/", lang) }];
                if (sec && sec.to !== path) items.push({ name: sec[k], url: urlFor(sec.to, lang) });
                items.push({ name: page.label[k], url: urlFor(path, lang) });
                ld.push(breadcrumbLd(items));
                if (page.template === "landing") {
                    const faq = v3LandingFaq(lang, path);
                    if (faq.length) ld.push(faqLd(faq));
                }
            }
            setJsonLd(ld);
            return;
        }
        {
            const sell = t?.r?.sell?.pages;
            const slug = path.split("/")[2];
            if (sell && slug && sell[slug]?.faq?.length) ld.push(faqLd(sell[slug].faq));
        }
        if (path !== "/" && known) {
            const titles = { ...(t?.r?.titles || {}), ...(V_TITLES?.[lang] || {}) };
            const items = [{ name: SITE.name, url: urlFor("/", lang) }];
            if (path.startsWith("/solutions/")) items.push({ name: titles["/solutions"], url: urlFor("/solutions", lang) });
            if (path.startsWith("/careers/")) items.push({ name: titles["/careers"], url: urlFor("/careers", lang) });
            items.push({ name: titles[path] || document.title.replace(/^Parto \| /, ""), url: urlFor(path, lang) });
            ld.push(breadcrumbLd(items));
        }
        setJsonLd(ld);
    }, [pathname, lang, t, version]);

    return null;
};

export default SeoLegacy;
